import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { redisConfigurationService, type RedisConfiguration } from '../../../../components/api/redisConfigurationService';

interface Props {
  onClose: () => void;
  onSubmit: () => void;
  initialData?: RedisConfiguration | null;
}

const AddRedisConfigModal: React.FC<Props> = ({ onClose, onSubmit, initialData }) => {
  const isEdit = Boolean(initialData?.id);

  const [name, setName] = useState(initialData?.name ?? '');
  const [connectionString, setConnectionString] = useState(initialData?.connectionString ?? '');
  const [host, setHost] = useState(initialData?.host ?? '');
  const [port, setPort] = useState<number | ''>(initialData?.port ?? '');
  const [database, setDatabase] = useState<number | ''>(initialData?.database ?? '');
  const [password, setPassword] = useState(initialData?.password ?? '');
  const [isActive, setIsActive] = useState(initialData?.isActive ?? true);
  const [description, setDescription] = useState(initialData?.description ?? '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      const dto = {
        name: name.trim(),
        connectionString: connectionString || undefined,
        host: host || undefined,
        port: port === '' ? undefined : Number(port),
        database: database === '' ? undefined : Number(database),
        password: password || undefined,
        isActive,
        description: description || undefined,
      };

      if (isEdit && initialData?.id) {
        await redisConfigurationService.update(initialData.id, dto);
      } else {
        await redisConfigurationService.create(dto);
      }
      onSubmit();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.message || err?.message || 'Failed to save Redis configuration.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex justify-center items-center p-4 overflow-y-auto">
      <div className="bg-neutral-900 w-full max-w-md rounded-xl shadow-xl border border-neutral-800 flex flex-col max-h-[90vh] my-auto">
        <div className="flex-shrink-0 flex justify-between items-center px-6 py-4 border-b border-neutral-800">
          <h2 className="text-white text-lg font-semibold">{isEdit ? 'Edit Redis Config' : 'Add Redis Config'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white" disabled={saving}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-4 text-sm text-white">
          {saveError && <p className="text-red-400 text-sm">{saveError}</p>}

          <div>
            <label className="text-gray-400 block mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Default Redis"
              className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
            />
          </div>

          <div>
            <label className="text-gray-400 block mb-1">Connection String (Optional)</label>
            <input
              type="text"
              value={connectionString}
              onChange={(e) => setConnectionString(e.target.value)}
              placeholder="e.g. localhost:6379"
              className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-gray-400 block mb-1">Host (Optional)</label>
              <input
                type="text"
                value={host}
                onChange={(e) => setHost(e.target.value)}
                placeholder="e.g. localhost"
                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
              />
            </div>
            <div>
              <label className="text-gray-400 block mb-1">Port (Optional)</label>
              <input
                type="number"
                value={port}
                onChange={(e) => setPort(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                placeholder="6379"
                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
              />
            </div>
          </div>

          <div>
            <label className="text-gray-400 block mb-1">Database (Optional)</label>
            <input
              type="number"
              value={database}
              onChange={(e) => setDatabase(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
              placeholder="0"
              className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
            />
          </div>

          <div>
            <label className="text-gray-400 block mb-1">Password (Optional)</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
            />
          </div>

          <div>
            <label className="text-gray-400 block mb-1">Description (Optional)</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description"
              className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={isActive}
              onChange={() => setIsActive(!isActive)}
              className="accent-indigo-500"
            />
            <label className="text-gray-300">Set as active</label>
          </div>
        </div>

        <div className="flex-shrink-0 flex justify-end gap-3 px-6 py-4 border-t border-neutral-800 bg-neutral-950 rounded-b-xl">
          <button onClick={onClose} className="text-gray-400 hover:text-white text-sm" disabled={saving}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!name.trim() || saving}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-md text-sm shadow disabled:opacity-40"
          >
            {saving ? 'Saving…' : isEdit ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddRedisConfigModal;
